import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupText,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DataTable from "react-data-table-component";
import { Triangle } from "react-loader-spinner";
import TableService from "../../../services/OrderingServices/TableService";
import { Link, Navigate, useNavigate } from "react-router-dom";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import CreateTable from "../component/CreateTable";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import Select from "react-select";
import useIsSmallScreen from "../../Payment Type/Component/small screen/SmallScreen";

import useTableSearch from "../../../hooks/useTableSearch";
import TableSearch from "../../../Components/Common/TableSearch";
import StatusBadge from "../../../Components/Common/Badge";
const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const TableList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const router = useNavigate();
  const isSmallScreen = useIsSmallScreen();

  const fetchTable = async () => {
    setLoading(true);
    try {
      const response = await TableService.getList();
      setData(response);
    } catch (error) {
      toast.error("Failed to fetch table data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTable();
  }, []);

  const toggleModal = (row = null) => {
    setSelectedTable(row);
    setIsModalOpen(!isModalOpen);
  };

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const handleDeleteClick = (id) => {
    setSelectedDeleteItem(id);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    try {
      const response = await TableService.delete(selectedDeleteItem);
      setData((prev) => prev.filter((item) => item.id !== selectedDeleteItem));
      toast.success(response);
    } catch (error) {
      console.log("🚀 ~ handleDelete ~ error:", error);
      toast.error(error?.response?.data?.details);
    } finally {
      toggleDeleteModal();
      setSelectedDeleteItem(null);
    }
  };

  const getStatusBadge = (e) => {
    const statusStyles = {
      Occupied: "bg-danger",
      Available: "bg-success",
      Reserved: "bg-dark",
      Unavailable: "bg-primary",
      UnderMaintenance: "bg-info",
    };
    return (
      <span className={`badge ${statusStyles[e] || "bg-danger"}`}>
        {e || "No Data"}
      </span>
    );
  };
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * rowsPerPage + (index + 1),
      width: "75px",
    },
    {
      name: "Name",
      selector: (row) => row.tableName,
      sortable: true,
    },
    {
      name: "Section Name",
      selector: (row) => row.sectionName,
      sortable: true,
    },
    {
      name: "Capacity",
      selector: (row) => row.capacity,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.tableStatus,
      cell: (row) => <StatusBadge status={row.tableStatus} type="table" pill />,

      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <ButtonGroup size="sm">
          <Link to={`/table/detail/${row.id}`}>
            <Button color="btn btn-soft-success" className="btn-sm">
              <i className="bx bx-show" />
            </Button>
          </Link>

          <Button
            color="btn btn-soft-warning"
            className="btn-sm ms-1"
            onClick={() => toggleModal(row)}
          >
            <i className="ri-edit-line" />
          </Button>
          <Button
            className="btn btn-soft-danger btn-sm ms-1"
            color="danger"
            onClick={() => handleDeleteClick(row.id)}
          >
            <i className="ri-delete-bin-5-line" />
          </Button>
        </ButtonGroup>
      ),
      center: true,
    },
  ];

  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchFilteredData,
  } = useTableSearch(data, [
    "tableName",
    "sectionName",
    "capacity",
    "tableStatus",
  ]);

  const [statusFilter, setStatusFilter] = useState(null);

  const handleStatusChange = (selectedOption) => {
    setStatusFilter(selectedOption);
  };

  // Apply BOTH search filter AND status filter
  const filteredData = searchFilteredData?.filter((item) => {
    // If no status filter selected, show all items
    if (!statusFilter) return true;

    // Otherwise, match the status
    return item.tableStatus === statusFilter.value;
  });

  const statusOptions = [
    { value: "Available", label: "Available" },
    { value: "Occupied", label: "Occupied" },
    { value: "Reserved", label: "Reserved" },
    { value: "Unavailable", label: "Unavailable" },
    { value: "UnderMaintenance", label: "UnderMaintenance" },
  ];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Table" pageTitle="System Setup" />

        <div
          className="p-3 p-lg-4 mt-1"
          style={{
            background: "#fff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          {/* Filter Button */}
          <Row className="justify-content-between align-content-center align-items-center ">
            <Col md={6} className="align-items-center">
              <h5 className="mb-1 fw-bold text-dark ">Tables List</h5>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex justify-content-end gap-2">
                <TableSearch
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by all.."
                />
                <InputGroup style={{ maxWidth: "300px" }}>
                  <Select
                    options={statusOptions}
                    value={statusFilter}
                    onChange={handleStatusChange}
                    placeholder="Select Status"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: "100%",
                      }),
                    }}
                  />
                </InputGroup>
                <Button
                  color="success"
                  onClick={() => toggleModal(null)}
                  className="d-flex align-items-center gap-2"
                  style={{ borderRadius: "8px" }}
                >
                  <i className="bx bx-plus"></i> Add
                </Button>
              </div>
            </Col>
          </Row>

          {isSmallScreen ? (
            loading ? (
              <div className="d-flex justify-content-center my-3">
                <Triangle
                  visible={true}
                  height="80"
                  width="80"
                  color="#5B71B9"
                />
              </div>
            ) : (
              <>
                {filteredData?.map((item, index) => (
                  <Card
                    key={item.id || index}
                    className="mb-3 shadow-sm d-block d-lg-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      router(`/table/detail/${item?.id}`);
                    }}
                  >
                    <CardBody>
                      <Row>
                        <Col xs={10}>
                          <h6 className="mb-2">Name: {`${item.tableName}`}</h6>
                          <h6 className="mb-2 text-muted">
                            Section: {item?.sectionName}
                          </h6>
                          <h6 className="mb-2 text-muted">
                            Capacity: {item?.capacity}
                          </h6>
                          <h6 className="mb-2 text-muted">
                            Status: {item?.tableStatus}
                          </h6>
                          <span
                            className={`badge ${
                              item.isActive ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </span>{" "}
                        </Col>
                        <Col xs={2}>
                          <div>
                            <Dropdown
                              isOpen={item.dropdownOpen || false}
                              toggle={(e) => {
                                e.stopPropagation();
                                setData((prev) =>
                                  prev.map((table) =>
                                    table.id === item.id
                                      ? {
                                          ...table,
                                          dropdownOpen: !table.dropdownOpen,
                                        }
                                      : { ...table, dropdownOpen: false }
                                  )
                                );
                              }}
                              direction="down"
                            >
                              <DropdownToggle
                                color="primary"
                                outline
                                className="btn-sm  shadow-sm rounded-circle"
                              >
                                <i className="ri-more-2-fill"></i>
                              </DropdownToggle>
                              <DropdownMenu end>
                                <DropdownItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleModal(item);
                                  }}
                                >
                                  <i className="ri-edit-line text-warning me-2"></i>{" "}
                                  Edit
                                </DropdownItem>
                                <DropdownItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(item.id);
                                  }}
                                >
                                  <i className="ri-delete-bin-5-line text-danger me-2"></i>{" "}
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                ))}
              </>
            )
          ) : (
            <CustomDataTable
              responsive
              striped
              pagination
              onChangePage={(page) => setCurrentPage(page)}
              onChangeRowsPerPage={(newPerPage) => {
                setRowsPerPage(newPerPage);
                setCurrentPage(1);
              }}
              fixedHeader
              persistTableHead
              progressPending={loading}
              customStyles={customStyles}
              progressComponent={
                <div className="my-3 text-center">
                  <Triangle
                    visible={true}
                    height="80"
                    width="80"
                    color="#5B71B9"
                  />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
              columns={columns}
              data={filteredData}
              noDataComponent={
                <div className="text-center my-5 w-100">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary">
                    No data Available
                  </h5>
                  <p className="text-muted mb-0">No table found.</p>
                </div>
              }
            />
          )}
        </div>

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={toggleDeleteModal}
        />

        <CreateTable
          isOpen={isModalOpen}
          toggle={toggleModal}
          selectedTable={selectedTable}
          refreshData={fetchTable}
        />
      </div>
    </Container>
  );
};

export default TableList;
